/**
 * TirvuArmazenamento — duas peças da fila offline em arquivo (services/FilaOffline.ts),
 * versão web. Mesma API de `modules/tirvu-armazenamento/index.ts` do app nativo
 * (`disponivel`, `lerValorGrandeDoAsyncStorage`, `gravarTextoComFsync`).
 *
 * No nativo existe por um caso real (05/10/2026): o AsyncStorage do Android não devolvia
 * valores > ~2 MB e a fila sumia. No web (docs/port/03 §9.5):
 *  - `disponivel = true`;
 *  - `lerValorGrandeDoAsyncStorage(chave)` → `AsyncStorage.getItem(chave)` — o IndexedDB não
 *    tem esse limite. `null` = não existe; `undefined` = não deu para ler. Nunca lança.
 *  - `gravarTextoComFsync(uri, texto)` → grava no FS virtual (IndexedDB). A transação do IDB
 *    só confirma depois de persistida — é o "fsync" do browser. `true` = gravado; LANÇA se a
 *    gravação falhou (ex.: cota cheia), e quem chama apaga o parcial.
 *
 * Extra web: `estimativaDeArmazenamento()` (`navigator.storage.estimate()`) e
 * `pedirArmazenamentoPersistente()` (`navigator.storage.persist()`), para a fila offline não
 * ser despejada pelo browser (03 §9.8).
 */
import AsyncStorage from "@/platform/async-storage";
import { writeAsStringAsync } from "@/platform/file-system";

/** true: a fila pode usar as duas peças (no web, sempre). */
export const disponivel = true;

/**
 * O valor da chave. `null` = a chave não existe; `undefined` = não deu para ler. Nunca lança.
 */
export async function lerValorGrandeDoAsyncStorage(chave: string): Promise<string | null | undefined> {
  try {
    const valor = await AsyncStorage.getItem(chave);
    return typeof valor === "string" ? valor : null;
  } catch (e) {
    console.warn("[TirvuArmazenamento] Leitura falhou:", e);
    return undefined;
  }
}

/**
 * Grava `texto` em `uri` e só resolve quando o IndexedDB confirmou a transação.
 * `true` = gravado; LANÇA se falhou — o arquivo pode ter ficado inconsistente, quem chama o apaga.
 */
export async function gravarTextoComFsync(uri: string, texto: string): Promise<boolean> {
  await writeAsStringAsync(uri, texto);
  return true;
}

// ── Extras web ──────────────────────────────────────────────────────────────

export type EstimativaArmazenamento = {
  /** Bytes usados pela origem (IndexedDB, cache do SW…), ou null se o browser não informa. */
  usadoBytes: number | null;
  /** Cota da origem em bytes, ou null. */
  cotaBytes: number | null;
  /** Bytes ainda disponíveis (cota − usado), ou null. */
  livreBytes: number | null;
  /** O browser prometeu não despejar os dados desta origem? */
  persistente: boolean;
};

export async function estimativaDeArmazenamento(): Promise<EstimativaArmazenamento> {
  const storage = typeof navigator !== "undefined" ? navigator.storage : undefined;
  let usado: number | null = null;
  let cota: number | null = null;
  let persistente = false;
  try {
    if (storage?.estimate) {
      const e = await storage.estimate();
      usado = typeof e.usage === "number" ? e.usage : null;
      cota = typeof e.quota === "number" ? e.quota : null;
    }
  } catch {
    // sem estimativa
  }
  try {
    if (storage?.persisted) persistente = await storage.persisted();
  } catch {
    // ignora
  }
  return {
    usadoBytes: usado,
    cotaBytes: cota,
    livreBytes: usado != null && cota != null ? Math.max(0, cota - usado) : null,
    persistente,
  };
}

/** Pede armazenamento persistente (não despejável). `true` = concedido. Nunca lança. */
export async function pedirArmazenamentoPersistente(): Promise<boolean> {
  try {
    const storage = typeof navigator !== "undefined" ? navigator.storage : undefined;
    if (!storage?.persist) return false;
    if (storage.persisted && (await storage.persisted())) return true;
    return await storage.persist();
  } catch {
    return false;
  }
}

const TirvuArmazenamento = {
  disponivel,
  lerValorGrandeDoAsyncStorage,
  gravarTextoComFsync,
  estimativaDeArmazenamento,
  pedirArmazenamentoPersistente,
};

export default TirvuArmazenamento;
